import {formatISO} from "date-fns";
import {
    useGeoJSONIncidents, useGeoJSONMicroZonesModel1AggregatedData,
    useGeoJSONModel1Stations,
    useGeoJSONModels23,
    useGeoJSONStations
} from "../../../components/maps/query";
import useSelectedFeatures from "../../../components/maps/GeoJSONMap/useSelectedFeatures";
import {useIncidentRegistryQuery, useIncidentSettingsQuery, useWebgisRegistryQuery} from "./query";
import {useMemo} from "react";
import {getContrastColor} from "./WebgisMap/markers";


const convertToIncidentAspect = (aspect) => {
    if (aspect === 'NORTH') return '2';
    if (aspect === 'NORTH_EAST') return '3';
    if (aspect === 'EAST') return '4';
    if (aspect === 'SOUTH_EAST') return '5';
    if (aspect === 'SOUTH') return '1';
    if (aspect === 'SOUTH_WEST') return '6';
    if (aspect === 'WEST') return '7';
    if (aspect === 'NORTH_WEST') return '8';
    return null;
}


export const formatFilterForAPI = {
    stations: (layers) => ({
        search: layers.commonFilters.search,
        workgroup_id__in: layers.commonFilters.workgroupIds,
        elevation__gte: layers.commonFilters.elevationGte ?? undefined,
        elevation__lte: layers.commonFilters.elevationLte ?? undefined,
        aspect__in: layers.commonFilters.aspects?.length > 0 ? layers.commonFilters.aspects.join(',') : undefined,
        visibility: layers.stations.filters?.visibility || undefined,
        province_id: layers.stations.filters?.province || undefined,
        municipality_id: layers.stations.filters?.municipality || undefined,
        type: layers.stations.filters?.type || undefined,
        micro_zone_id__in: layers.commonFilters.microZones?.map(m => m.id).join(',') || undefined,
        currently_active: layers.stations.filters?.currentlyActive === "" ? undefined : layers.stations.filters?.currentlyActive,
        accept_mod1: layers.stations.filters?.acceptsModel1 === "" ? undefined : layers.stations.filters?.acceptsModel1,
        accept_mod23: layers.stations.filters?.acceptsModel23 === "" ? undefined : layers.stations.filters?.acceptsModel23,
    }),

    microzones: (layers) => ({
        search: layers.commonFilters.search,
        model1__datetime__gte: layers.commonFilters.dateGte ? formatISO(layers.commonFilters.dateGte) : undefined,
        model1__datetime__lte: layers.commonFilters.dateLte ? formatISO(layers.commonFilters.dateLte) : undefined,
        workgroup_id__in: layers.commonFilters.workgroupIds,
        id__in: layers.commonFilters.microZones?.map(m => m.id).join(',') || undefined,
    }),

    models23: (layers) => ({
        search: layers.commonFilters.search,
        station__workgroup_id__in: layers.commonFilters.workgroupIds,
        datetime__gte: layers.commonFilters.dateGte ? formatISO(layers.commonFilters.dateGte) : undefined,
        datetime__lte: layers.commonFilters.dateLte ? formatISO(layers.commonFilters.dateLte) : undefined,
        elevation__gte: layers.commonFilters.elevationGte ?? undefined,
        elevation__lte: layers.commonFilters.elevationLte ?? undefined,
        aspect__in: layers.commonFilters.aspects?.length > 0 ? layers.commonFilters.aspects.join(',') : undefined,
        station__type: layers.models23.filters?.stationType || undefined,
        status__in: layers.models23.filters?.status || undefined,
        user__id__in: layers.models23.filters?.user?.map(u => u.id).join(',') || undefined,
        station__acl__model23_write: layers.models23.filters?.stationAclModel23Write || undefined,
        micro_zone_id__in: layers.commonFilters.microZones?.map(m => m.id).join(',') || undefined,
    }),

    model1Stations: (layers) => ({
        search: layers.commonFilters.search,
        station__workgroup_id__in: layers.commonFilters.workgroupIds,
        datetime__gte: layers.commonFilters.dateGte ? formatISO(layers.commonFilters.dateGte) : undefined,
        datetime__lte: layers.commonFilters.dateLte ? formatISO(layers.commonFilters.dateLte) : undefined,
        elevation__gte: layers.commonFilters.elevationGte ?? undefined,
        elevation__lte: layers.commonFilters.elevationLte ?? undefined,
        station__aspect__in: layers.commonFilters.aspects?.length > 0 ? layers.commonFilters.aspects.join(',') : undefined,
        station__type: layers.model1Stations.filters?.stationType || undefined,
        status__in: layers.model1Stations.filters?.status || undefined,
        validation_status__in: layers.model1Stations.filters?.validationStatus || undefined,
        user__id__in: layers.model1Stations.filters?.user?.map(u => u.id).join(',') || undefined,
        station__acl__model1_write: layers.model1Stations.filters?.stationAclModel1Write || undefined,
        micro_zone_id__in: layers.commonFilters.microZones?.map(m => m.id).join(',') || undefined,
    }),

    incidents: (layers) => ({
        search: layers.commonFilters.search,
        workgroup_id__in: layers.commonFilters.workgroupIds,
        date__gte: layers.commonFilters.dateGte ? formatISO(layers.commonFilters.dateGte) : undefined,
        date__lte: layers.commonFilters.dateLte ? formatISO(layers.commonFilters.dateLte) : undefined,
        elevation_release__gte: layers.commonFilters.elevationGte ?? undefined,
        elevation_release__lte: layers.commonFilters.elevationLte ?? undefined,
        aspect__in: layers.commonFilters.aspects?.length > 0 ? layers.commonFilters.aspects.map(convertToIncidentAspect).filter(a => a !== null).join(',') : undefined,
        incident_category: layers.incidents.filters?.incidentCategory || undefined,
        incident_subcategory: layers.incidents.filters?.incidentSubcategory || undefined,
        region_id: layers.incidents.filters?.region || undefined,
        province_id: layers.incidents.filters?.province || undefined,
        municipality_id: layers.incidents.filters?.municipality || undefined,
        micro_zone_id__in: layers.commonFilters.microZones?.map(m => m.id).join(',') || undefined,
    })
}



const useFeaturesState = ({layersState}) => {
    const {layers} = layersState;

    // Stations Data
    const geoStationQuery = useGeoJSONStations(formatFilterForAPI.stations(layers), {enabled: layers.stations.active});
    const stationGeoJSON = geoStationQuery.isFetching ? {type: "FeatureCollection", features: []} : (geoStationQuery.data ?? {type: "FeatureCollection", features: []});
    const stationSelectedState = useSelectedFeatures({features: stationGeoJSON.features, multiple: true, multiSelectKey: 'shift', keepSelectedIfMissing: true});

    // Microzones data
    const geoMicroZoneQuery = useGeoJSONMicroZonesModel1AggregatedData(formatFilterForAPI.microzones(layers), {enabled: layers.microzones.active});
    const microZonesGeoJSON = geoMicroZoneQuery.isFetching ? {type: "FeatureCollection", features: []} : (geoMicroZoneQuery.data ?? {type: "FeatureCollection", features: []});

    // Model23 data
    const geoModel23Query = useGeoJSONModels23(formatFilterForAPI.models23(layers), {enabled: layers.models23.active});
    const model23GeoJSON = geoModel23Query.isFetching ? {type: "FeatureCollection", features: []} : (geoModel23Query.data ?? {type: "FeatureCollection", features: []});
    const model23SelectedState = useSelectedFeatures({features: model23GeoJSON.features, multiple: true, multiSelectKey: 'shift', keepSelectedIfMissing: true});

    // Model1 data
    const geoModel1StationsQuery = useGeoJSONModel1Stations(formatFilterForAPI.model1Stations(layers), {enabled: layers.model1Stations.active});
    const model1StationsGeoJSON = geoModel1StationsQuery.isFetching ? {type: "FeatureCollection", features: []} : (geoModel1StationsQuery.data ?? {type: "FeatureCollection", features: []});
    const model1SelectedState = useSelectedFeatures({features: model1StationsGeoJSON.features, multiple: true, multiSelectKey: 'shift', keepSelectedIfMissing: true});

    // incidents data
    const geoIncidentsQuery = useGeoJSONIncidents(formatFilterForAPI.incidents(layers), {enabled: layers.incidents.active});
    const incidentsGeoJSON = geoIncidentsQuery.isFetching ? {type: "FeatureCollection", features: []} : (geoIncidentsQuery.data ?? {type: "FeatureCollection", features: []});
    const incidentSelectedState = useSelectedFeatures({features: incidentsGeoJSON.features, multiple: true, multiSelectKey: 'shift', keepSelectedIfMissing: true});

    // Incidents settings
    const incidentSettingQuery = useIncidentSettingsQuery();
    const incidentRegistryQuery = useIncidentRegistryQuery();

    // Webgis Registry (Legends)
    const webgisRegistryQuery = useWebgisRegistryQuery();

    const contrastColorsHash = useMemo(() => {
        const hash = {};
        const registry = webgisRegistryQuery.data ?? {};

        Object.values(registry).forEach(layerRegistry => {
            if (layerRegistry.legends) {
                Object.values(layerRegistry.legends).forEach(legendItems => {
                    legendItems.forEach(item => {
                        if (item.color && !hash[item.color]) {
                            hash[item.color] = getContrastColor(item.color);
                        }
                    });
                });
            }
        });

        // Add default colors
        const defaultColors = ["#1e88e5", "#6e6e6e"];
        defaultColors.forEach(color => {
            if (!hash[color]) {
                hash[color] = getContrastColor(color);
            }
        });

        return hash;
    }, [webgisRegistryQuery.data]);

    return {
        geoStationQuery,
        geoMicroZoneQuery,
        geoModel23Query,
        geoModel1StationsQuery,
        geoIncidentsQuery,

        stationGeoJSON,
        microZonesGeoJSON,
        model1StationsGeoJSON,
        model23GeoJSON,
        incidentsGeoJSON,

        stationSelectedState,
        model23SelectedState,
        model1SelectedState,
        incidentSelectedState,

        incidentSettingQuery,
        incidentRegistryQuery,
        webgisRegistryQuery,

        contrastColorsHash,

        formatFilterForAPI
    }
}

export default useFeaturesState;